import { useMutation } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Panel } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Network } from "lucide-react";

const LABELS = {
  firewall_permits_connection: "Firewall lets this app reach egress.tradifyai.in:9443",
  server_certificate_trusted: "Gateway server certificate trusted (hostname verified)",
  no_client_cert_rejected: "Connection without a client certificate rejected",
  untrusted_client_cert_rejected: "Connection with an untrusted client certificate rejected",
};

function Row({ ok, label, testId }) {
  return (
    <div data-testid={testId} className="flex items-center gap-2">
      <span className={ok ? "text-emerald-400" : "text-rose-400"}>{ok ? "PASS" : "FAIL"}</span>
      <span className="text-slate-300">{label}</span>
    </div>
  );
}

function Result({ d }) {
  const pin = d.pinned_client_cert;
  return (
    <div data-testid="egress-probe-result" className="space-y-1.5">
      {Object.entries(LABELS).map(([k, l]) => <Row key={k} testId={`egress-check-${k}`} ok={d.checks[k]} label={l} />)}
      <div data-testid="egress-pinned" className="text-slate-400">
        Pinned app certificate: {pin.configured ? (pin.accepted ? "accepted" : "NOT accepted") : "not installed yet (expected)"}
        {pin.accepted && <span data-testid="egress-order-blocked"> · order route with valid cert: {pin.order_route_blocked ? "blocked (403)" : "NOT blocked"}</span>}
      </div>
      <div data-testid="egress-ip" className="text-slate-500 font-mono">
        Outgoing IP {d.egress_ip || "?"} (dynamic, shared NAT — access is controlled by the pinned client certificate) · server {d.served_host}
      </div>
      {d.server_tls && <div className="text-slate-500 font-mono">{d.server_tls.tls_version} · issuer {d.server_tls.issuer} · expires {d.server_tls.not_after}</div>}
      <div data-testid="egress-env" className="text-slate-500 font-mono break-all">
        Environment {d.environment} · backend instance {d.backend_instance} · API {process.env.REACT_APP_BACKEND_URL}
      </div>
      {d.egress_ip_in_allowlist === false && !d.checks.firewall_permits_connection && (
        <div data-testid="egress-preview-note" className="text-amber-400">
          {d.environment === "production"
            ? "Published backend's outgoing IP is not in the firewall allowlist — firewall blocked the connection."
            : "Blocked by the firewall allowlist — expected outside the published app."}
        </div>
      )}
      <div className="text-slate-500">Broker requests sent: {d.broker_requests_sent} · credentials sent: no</div>
      <div data-testid="egress-overall" className={d.all_ok ? "text-emerald-400" : "text-rose-400"}>{d.all_ok ? "RESULT pass" : "RESULT fail"}</div>
    </div>
  );
}

export function EgressConnectivityPanel() {
  const m = useMutation({ mutationFn: async () => (await api.post("/system/egress-connectivity")).data });
  return (
    <Panel title="Gateway :9443 connectivity test (no broker calls)" testId="egress-connectivity-panel" actions={<Network className="w-4 h-4 text-slate-500" />}>
      <div className="p-4 space-y-3 text-xs">
        <Button data-testid="egress-probe-run-btn" size="sm" onClick={() => m.mutate()} disabled={m.isPending}>
          {m.isPending ? "Testing…" : "Run connectivity test"}
        </Button>
        {m.isError && <div data-testid="egress-probe-error" className="text-rose-400">Test could not run.</div>}
        {m.data && <Result d={m.data} />}
      </div>
    </Panel>
  );
}
